import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { convertToDisplayDate } from "../../utils/date";

interface LeadDifference {
  field: string;
  currentValue: unknown;
  duplicateValue: unknown;
}

interface DuplicateLeadDifferencesSectionProps {
  currentLead: Record<string, any>;
  duplicateLeads: Record<string, any>[];
  isLoading: boolean;
  error: string;
  getDifferences: (
    currentLead: Record<string, any>,
    duplicateLead: Record<string, any>,
  ) => LeadDifference[];
  formatFieldName: (field: string) => string;
  formatValue: (value: unknown) => string;
  canUseDuplicateValue: (field: string) => boolean;
  onUseDuplicateValue: (field: string, value: unknown) => void;
  onExpandedChange: (expanded: boolean) => void;
}

const DuplicateLeadDifferencesSection = ({
  currentLead,
  duplicateLeads,
  isLoading,
  error,
  getDifferences,
  formatFieldName,
  formatValue,
  canUseDuplicateValue,
  onUseDuplicateValue,
  onExpandedChange,
}: DuplicateLeadDifferencesSectionProps) => {
  const [expanded, setExpanded] = useState(false);

  const toggle = () => {
    const nextExpanded = !expanded;
    setExpanded(nextExpanded);
    onExpandedChange(nextExpanded);
  };

  return (
    <div className="rounded-md border border-red-200 bg-red-50 p-2">
      <button
        type="button"
        onClick={toggle}
        className="flex w-full items-center justify-between text-left font-bold text-red-800 cursor-pointer"
      >
        <span>
          Duplicate Lead Differences
          {duplicateLeads.length > 0 ? ` (${duplicateLeads.length})` : ""}
        </span>
        <ChevronDown
          className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`}
        />
      </button>

      {expanded && (
        <div className="mt-2 space-y-2 text-red-900">
          {isLoading && duplicateLeads.length === 0 && (
            <p>Loading duplicate leads...</p>
          )}
          {isLoading && duplicateLeads.length > 0 && (
            <p className="text-xs text-red-600">Refreshing...</p>
          )}
          {error ? (
            <p className="text-red-600">{error}</p>
          ) : (
            <>
              {!isLoading && duplicateLeads.length === 0 && (
                <p className="rounded-md border border-gray-200 bg-white p-2 text-xs text-gray-600">
                  no duplicate found
                </p>
              )}
              {duplicateLeads.map((duplicateLead, duplicateIndex) => {
                const differences = getDifferences(currentLead, duplicateLead);

                return (
                  <article
                    key={duplicateLead.ID || duplicateLead.id}
                    className="rounded-md border border-red-200 bg-white p-2 shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-semibold text-red-950">
                        Duplicate {duplicateIndex + 1}
                      </p>
                      <span className="rounded-full bg-red-100 px-1 py-0.5 text-xs font-medium text-red-800">
                        {convertToDisplayDate(
                          duplicateLead.createdDate || duplicateLead.createdAt,
                        ) || "Date not available"}
                      </span>
                    </div>

                    {differences.length === 0 ? (
                      <p className="mt-3 rounded bg-gray-50 p-2 text-xs text-gray-600">
                        No differing fields were found.
                      </p>
                    ) : (
                      <div className="mt-2 space-y-2">
                        {differences.map((difference) => (
                          <div
                            key={difference.field}
                            className="rounded-md border border-gray-300 p-2 text-xs text-gray-700"
                          >
                            <p className="mb-1 font-semibold text-gray-900">
                              {formatFieldName(difference.field)}:
                            </p>
                            <div className="grid grid-cols-2 gap-2">
                              <div className="min-w-0 rounded bg-gray-50 p-1">
                                <p className="mb-1 font-medium text-gray-500">
                                  Current
                                </p>
                                <p className="break-words text-gray-900">
                                  {formatValue(difference.currentValue)}
                                </p>
                              </div>
                              <div className="min-w-0 rounded bg-red-50 p-1">
                                <p className="mb-1 font-medium text-red-500">
                                  Duplicate
                                </p>
                                <p className="break-words text-gray-900">
                                  {formatValue(difference.duplicateValue)}
                                </p>
                              </div>
                            </div>
                            {canUseDuplicateValue(difference.field) && (
                              <button
                                type="button"
                                onClick={() =>
                                  onUseDuplicateValue(
                                    difference.field,
                                    difference.duplicateValue,
                                  )
                                }
                                className="mt-1 rounded bg-blue-600 px-2 py-1 font-medium text-white hover:bg-blue-700"
                              >
                                Use duplicate value
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </article>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default DuplicateLeadDifferencesSection;
